import {action, makeObservable, observable} from 'mobx';

export interface IRecentDynamicFormula {
    name: string;
    expression: string;
}

export const MAX_RECENT_DYNAMIC_FORMULAS = 10;
const MAX_NAME_LENGTH = 200;
const MAX_EXPRESSION_LENGTH = 2000;

const sameExpression = (a: string, b: string) =>
    a.replace(/\s/g, '').toLowerCase() === b.replace(/\s/g, '').toLowerCase();

/** The user's last created dynamic formulas, newest first, kept per user in localStorage. */
export default class RecentDynamicFormulas {
    @observable.ref public items: IRecentDynamicFormula[] = [];
    private storageKey: string | null = null;

    constructor() {
        makeObservable(this);
    }

    @action public initialize(userId: string) {
        this.storageKey = userId ? `reporting-recent-formulas:v1:${userId}` : null;
        this.items = [];
        if (!this.storageKey) return;
        try {
            this.items = this.bound(JSON.parse(window.localStorage.getItem(this.storageKey) || '[]'));
        } catch {
            // Private browsing, disabled storage, or corrupt data: start in memory.
        }
    }

    /** Moves an existing formula with the same expression to the top instead of duplicating it. */
    @action public record(name: string, expression: string) {
        const item = {name: name.trim(), expression: expression.trim()};
        if (!item.name || !item.expression) return;
        this.save([item, ...this.items.filter(existing => !sameExpression(existing.expression, item.expression))]);
    }

    @action public remove(expression: string) {
        this.save(this.items.filter(item => item.expression !== expression));
    }

    private save(items: IRecentDynamicFormula[]) {
        this.items = this.bound(items);
        if (!this.storageKey) return;
        try {
            window.localStorage.setItem(this.storageKey, JSON.stringify(this.items));
        } catch {
            // Keep the list in memory if storage is full or blocked.
        }
    }

    private bound(value: unknown): IRecentDynamicFormula[] {
        if (!Array.isArray(value)) return [];
        return value
            .filter((item): item is IRecentDynamicFormula => !!item && typeof item === 'object'
                && typeof item.name === 'string' && typeof item.expression === 'string'
                && !!item.name.trim() && !!item.expression.trim()
                && item.name.length <= MAX_NAME_LENGTH && item.expression.length <= MAX_EXPRESSION_LENGTH)
            .map(({name, expression}) => ({name, expression}))
            .slice(0, MAX_RECENT_DYNAMIC_FORMULAS);
    }
}
